/**
 * Query service for the provider calendar.
 *
 * Searches `Appointment` resources where selected practitioners are actors and
 * `date` falls within the visible window. One actor search per practitioner
 * (comma-OR is not assumed).
 */

import type { MedplumClient } from '@medplum/core';
import { formatHumanName } from '@medplum/core';
import type { Appointment, Patient, QuestionnaireResponse } from '@medplum/fhirtypes';
import { APPOINTMENT_TAG_SYSTEM } from '@unify/fhir-model/constants';
import { getServiceTypeCode } from '@unify/fhir-model/resources/episode-of-care';
import { getDetailedStatus } from '@unify/fhir-model/resources/questionnaire-response';
import {
  appointmentsToCalendarEvents,
  type AppointmentMapperOptions,
} from './appointmentToCalendarEvent';
import { isClinicalNoteLockedStatus } from './calendarEventGuards';
import type { ProviderCalendarEvent, ProviderCalendarWindow } from './types';
import { loadPractitionerStaffLabelMap } from '../../lib/loadPractitionerPreferredLabels';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

/** Appointment statuses we want to surface on a provider's calendar. */
export const PROVIDER_CALENDAR_APPOINTMENT_STATUSES: ReadonlyArray<Appointment['status']> = [
  'proposed',
  'booked',
  'fulfilled',
  'cancelled',
  'noshow',
];

const DEFAULT_CONCURRENCY = 4;
const EPISODE_TYPE_ENRICHMENT_BATCH_SIZE = 100;
const PATIENT_READ_BATCH_SIZE = 50;

export interface GetProviderCalendarEventsArgs extends AppointmentMapperOptions {
  /** Medplum client (required for live queries). */
  medplum: Pick<MedplumClient, 'searchResources'>;
  /**
   * Practitioner ids whose calendars to load. Prefer the selected set after
   * access resolution — do not pass the full accessible roster by default.
   * Required unless deprecated `practitionerId` is set.
   */
  practitionerIds?: readonly string[];
  /**
   * @deprecated Prefer `practitionerIds`. Kept for single-calendar callers.
   */
  practitionerId?: string;
  /** Visible calendar window (e.g. the current week/day/month). */
  window: ProviderCalendarWindow;
  /**
   * Optional status restriction. Defaults to
   * {@link PROVIDER_CALENDAR_APPOINTMENT_STATUSES}.
   */
  statuses?: ReadonlyArray<Appointment['status']>;
  /** Max rows to pull per actor search. */
  count?: number;
  /** Max concurrent actor searches. */
  concurrency?: number;
  /**
   * When true (default), probe readable Patient ids and redact inaccessible
   * patient details to `Busy`.
   */
  applyPatientPrivacy?: boolean;
}

export interface GetProviderCalendarEventsResult {
  events: ProviderCalendarEvent[];
  /** Practitioner ids whose actor search failed. */
  failedPractitionerIds: string[];
}

/** True when an event should render for the selected EoC-type filter (empty = none). */
export function calendarEventMatchesEocTypeFilter(
  event: Pick<ProviderCalendarEvent, 'episodeOfCareTypeCode' | 'appointmentCategory'>,
  selectedTypeCodes: readonly string[],
): boolean {
  if (selectedTypeCodes.length === 0) return false;
  const code = event.episodeOfCareTypeCode;
  /** Fail open when the type is unknown so enrichment gaps do not hide real sessions. */
  if (!code) return true;
  return selectedTypeCodes.includes(code);
}

async function fetchEpisodeTypeCodeMap(
  medplum: Pick<MedplumClient, 'searchResources'>,
  episodeIds: string[],
): Promise<Map<string, string>> {
  const typeCodeByEpisodeId = new Map<string, string>();
  for (let offset = 0; offset < episodeIds.length; offset += EPISODE_TYPE_ENRICHMENT_BATCH_SIZE) {
    const batch = episodeIds.slice(offset, offset + EPISODE_TYPE_ENRICHMENT_BATCH_SIZE);
    const episodes = await medplum.searchResources('EpisodeOfCare', {
      _id: batch.join(','),
      _count: String(batch.length),
    });
    for (const episode of episodes) {
      if (!episode.id) continue;
      const code = getServiceTypeCode(episode);
      if (code) typeCodeByEpisodeId.set(episode.id, code);
    }
  }
  return typeCodeByEpisodeId;
}

/** Resolve EpisodeOfCare.type codes for calendar events that reference an episode. */
export async function enrichCalendarEventsWithEpisodeTypeCodes(
  medplum: Pick<MedplumClient, 'searchResources'>,
  events: ProviderCalendarEvent[],
): Promise<ProviderCalendarEvent[]> {
  const episodeIds = [
    ...new Set(events.map((event) => event.episodeOfCareId).filter((id): id is string => Boolean(id))),
  ];
  if (episodeIds.length === 0) return events;

  try {
    const typeCodeByEpisodeId = await fetchEpisodeTypeCodeMap(medplum, episodeIds);
    return events.map((event) => {
      if (!event.episodeOfCareId) return event;
      const episodeOfCareTypeCode = typeCodeByEpisodeId.get(event.episodeOfCareId);
      return episodeOfCareTypeCode ? { ...event, episodeOfCareTypeCode } : event;
    });
  } catch (err) {
    console.warn('[getProviderCalendarEvents] EpisodeOfCare enrichment failed:', err);
    return events;
  }
}

/** Mark events whose linked clinical note is signed or cosigned. */
export async function enrichCalendarEventsWithSignedNotes(
  medplum: Pick<MedplumClient, 'searchResources'>,
  events: ProviderCalendarEvent[],
  concurrency = DEFAULT_CONCURRENCY,
): Promise<ProviderCalendarEvent[]> {
  const appointmentIds = [...new Set(events.map((event) => event.id).filter(Boolean))];
  if (appointmentIds.length === 0) return events;

  const signedIds = new Set<string>();
  await mapPool(appointmentIds, concurrency, async (appointmentId) => {
    try {
      const responses = await medplum.searchResources('QuestionnaireResponse', {
        _tag: `${APPOINTMENT_TAG_SYSTEM}|${appointmentId}`,
        _count: '20',
      }) as QuestionnaireResponse[];
      if (responses.some((qr) => isClinicalNoteLockedStatus(getDetailedStatus(qr)))) {
        signedIds.add(appointmentId);
      }
    } catch (err) {
      console.warn(`[getProviderCalendarEvents] Signed-note search failed for ${appointmentId}:`, err);
    }
  });

  if (signedIds.size === 0) return events;
  return events.map((event) => (
    signedIds.has(event.id) ? { ...event, linkedNoteSigned: true } : event
  ));
}

/** Build a Medplum `date=ge…&date=lt…` range filter from a window. */
export function buildAppointmentDateRangeParams(window: ProviderCalendarWindow): {
  date: string[];
} {
  const startISO = window.start.toISOString();
  const endISO = window.end.toISOString();
  return {
    date: [`ge${startISO}`, `lt${endISO}`],
  };
}

async function searchAppointmentsForActor(
  medplum: Pick<MedplumClient, 'searchResources'>,
  practitionerId: string,
  date: string[],
  statuses: ReadonlyArray<Appointment['status']>,
  count: number,
): Promise<Appointment[]> {
  const search = new URLSearchParams();
  search.set('actor', `Practitioner/${practitionerId}`);
  for (const d of date) search.append('date', d);
  search.set('status', statuses.join(','));
  search.set('_sort', 'date');
  search.set('_count', String(count));
  return medplum.searchResources('Appointment', search);
}

async function mapPool<T, R>(
  items: readonly T[],
  concurrency: number,
  worker: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await worker(items[index]);
    }
  });
  await Promise.all(runners);
  return results;
}

function collectPatientIdsFromAppointments(appointments: Appointment[]): string[] {
  const ids = new Set<string>();
  for (const appt of appointments) {
    for (const p of appt.participant ?? []) {
      const ref = p.actor?.reference;
      if (ref?.startsWith('Patient/')) {
        const id = ref.slice('Patient/'.length).trim();
        if (id) ids.add(id);
      }
    }
  }
  return [...ids];
}

async function resolveReadablePatients(
  medplum: Pick<MedplumClient, 'searchResources'>,
  patientIds: string[],
): Promise<{ readableIds: Set<string>; displayById: Map<string, string> }> {
  const readableIds = new Set<string>();
  const displayById = new Map<string, string>();
  if (patientIds.length === 0) return { readableIds, displayById };
  for (let offset = 0; offset < patientIds.length; offset += PATIENT_READ_BATCH_SIZE) {
    const batch = patientIds.slice(offset, offset + PATIENT_READ_BATCH_SIZE);
    try {
      const patients = (await medplum.searchResources('Patient', {
        _id: batch.join(','),
        _count: String(batch.length),
      })) as Patient[];
      for (const patient of patients) {
        if (!patient.id) continue;
        readableIds.add(patient.id);
        const name =
          (patient.name?.[0] ? formatHumanName(patient.name[0]) : undefined)?.trim() ||
          patient.name?.[0]?.text?.trim();
        if (name) displayById.set(patient.id, name);
      }
    } catch (err) {
      console.warn('[getProviderCalendarEvents] Patient privacy/display probe failed:', err);
    }
  }
  return { readableIds, displayById };
}

/**
 * Load Appointments for one or more practitioners within the visible window.
 */
export async function getProviderCalendarEvents(
  args: GetProviderCalendarEventsArgs,
): Promise<ProviderCalendarEvent[]> {
  const result = await getProviderCalendarEventsDetailed(args);
  return result.events;
}

/**
 * Multi-practitioner fetch with partial-failure reporting.
 */
export async function getProviderCalendarEventsDetailed(
  args: GetProviderCalendarEventsArgs,
): Promise<GetProviderCalendarEventsResult> {
  const practitionerIds = [
    ...new Set(
      (args.practitionerIds?.length
        ? args.practitionerIds
        : args.practitionerId
          ? [args.practitionerId]
          : []
      ).filter(Boolean),
    ),
  ];
  if (practitionerIds.length === 0) {
    return { events: [], failedPractitionerIds: [] };
  }

  const statuses = args.statuses ?? PROVIDER_CALENDAR_APPOINTMENT_STATUSES;
  const count = args.count ?? 200;
  const concurrency = args.concurrency ?? DEFAULT_CONCURRENCY;
  const { date } = buildAppointmentDateRangeParams(args.window);
  const { medplum } = args;

  const failedPractitionerIds: string[] = [];
  const byAppointmentId = new Map<string, Appointment>();

  const settled = await mapPool(practitionerIds, concurrency, async (practitionerId) => {
    try {
      const appointments = await searchAppointmentsForActor(
        medplum,
        practitionerId,
        date,
        statuses,
        count,
      );
      return { practitionerId, appointments, error: null as string | null };
    } catch (err) {
      const message = getUserFacingErrorMessage(err, 'search failed');
      console.warn(`[getProviderCalendarEvents] actor=${practitionerId} failed:`, err);
      return { practitionerId, appointments: [] as Appointment[], error: message };
    }
  });

  for (const row of settled) {
    if (row.error) failedPractitionerIds.push(row.practitionerId);
    for (const appt of row.appointments) {
      if (appt.id && !byAppointmentId.has(appt.id)) {
        byAppointmentId.set(appt.id, appt);
      }
    }
  }

  const appointments = [...byAppointmentId.values()];
  const appointmentPractitionerIds = [
    ...new Set(
      appointments.flatMap((appointment) =>
        (appointment.participant ?? []).flatMap((participant) => {
          const ref = participant.actor?.reference;
          return ref?.startsWith('Practitioner/')
            ? [ref.slice('Practitioner/'.length)]
            : [];
        }),
      ),
    ),
  ];
  const practitionerDisplayById = await loadPractitionerStaffLabelMap(
    medplum,
    appointmentPractitionerIds,
  );
  const applyPrivacy = args.applyPatientPrivacy !== false;
  const patientIds = collectPatientIdsFromAppointments(appointments);
  const { readableIds, displayById } = await resolveReadablePatients(medplum, patientIds);
  const readablePatientIds = applyPrivacy ? readableIds : undefined;

  const primaryId = args.currentPractitionerId ?? practitionerIds[0];
  const events = appointmentsToCalendarEvents(appointments, {
    currentPractitionerId: primaryId,
    currentProviderDisplayLabel: args.currentProviderDisplayLabel,
    canManageByOwnerId: args.canManageByOwnerId,
    readablePatientIds,
    patientDisplayById: displayById,
    practitionerDisplayById,
  });

  const enriched = await enrichCalendarEventsWithEpisodeTypeCodes(medplum, events);
  const withSignedNotes = await enrichCalendarEventsWithSignedNotes(medplum, enriched);
  return { events: withSignedNotes, failedPractitionerIds };
}
